import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import { Badge, Button, Card, Col, Form, InputGroup, Row, Spinner } from 'react-bootstrap';
import axiosInstance from '../services/axiosInterceptor';

const todayISO = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api').replace(/\/api\/?$/, '').replace(/\/$/, '');
const photoUrl = (photo) => (photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null);

const thaiDate = (value) => (value ? new Date(value).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' }) : '-');
const thaiTime = (value) => (value ? new Date(value).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }) : '');
const clockNow = () => new Date().toTimeString().slice(0, 5);

// สถานะที่ยามต้องเห็นชัดๆ
//   ready    = อนุมัติแล้ว ยังไม่ออก → กดปุ่ม "ออกแล้ว" ได้
//   out      = ออกไปแล้ว รอกดปุ่ม "กลับเข้าแล้ว"
//   done     = กลับเข้าโรงเรียนแล้ว
//   waiting  = รอฝ่ายปกครองอนุมัติ
//   blocked  = ปฏิเสธ/ยกเลิก
// ใบกลับบ้านมีขั้นกลับเข้าเหมือนใบออกนอกแล้ว ต่างกันแค่กลับข้ามวันได้
const gateState = (req) => {
  if (req.status !== 'approved') return req.status === 'pending' ? 'waiting' : 'blocked';
  if (req.checked_in_at) return 'done';
  return req.checked_out_at ? 'out' : 'ready';
};

// เลยกำหนดกลับหรือยัง — ใบออกนอกดูเป็นเวลาในวันเดียว ใบกลับบ้านดูเป็นวันที่
const isOverdue = (req) => {
  if (gateState(req) !== 'out') return false;
  if (req.pass_type === 'home') return !!req.return_date && todayISO() > req.return_date;
  return req.pass_date === todayISO() && !!req.expect_return_time && clockNow() > req.expect_return_time;
};

const STATE_STYLE = {
  ready: { border: 'border-success', badge: 'success', label: 'อนุมัติแล้ว — ออกได้' },
  out: { border: 'border-warning', badge: 'warning', label: 'ออกไปแล้ว — รอกลับเข้าโรงเรียน' },
  done: { border: 'border-secondary', badge: 'secondary', label: 'เสร็จสิ้น' },
  waiting: { border: 'border-warning', badge: 'warning', label: 'รอฝ่ายปกครองอนุมัติ — ยังออกไม่ได้' },
  blocked: { border: 'border-danger', badge: 'danger', label: 'ไม่อนุมัติ/ยกเลิก — ห้ามออก' }
};

const PASS_TYPE = {
  home: { label: 'กลับบ้าน', variant: 'primary', icon: 'ri-home-heart-line' },
  outing: { label: 'ออกนอกสถานที่ (ไป-กลับวันนี้)', variant: 'info', icon: 'ri-walk-line' }
};

const passKey = (req) => `${req.pass_type}-${req.pass_id}`;

const StudentPhoto = ({ photo, name, size = 56 }) => {
  const src = photoUrl(photo);
  return src ? (
    <img
      src={src}
      alt={name}
      width={size}
      height={size}
      className="rounded-circle border bg-white flex-shrink-0"
      style={{ objectFit: 'cover' }}
    />
  ) : (
    <div
      className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center flex-shrink-0 fw-semibold"
      style={{ width: size, height: size, fontSize: size / 2.4 }}
      aria-hidden="true"
    >
      {name?.trim().charAt(0) || '?'}
    </div>
  );
};

export default function GuardGatePass() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const [date, setDate] = useState(todayISO());
  const [keyword, setKeyword] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [rows, setRows] = useState([]);
  // ใบที่ออกไปแล้วยังไม่กลับ ข้ามวันได้ จึงโหลดแยกจากรายการที่กรองตามวันที่
  const [openRows, setOpenRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('ready');
  const [savingKey, setSavingKey] = useState(null);

  const fetchList = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    const params = new URLSearchParams();
    if (searchTerm) params.set('keyword', searchTerm);
    else params.set('date', date);

    axiosInstance.get(`/api/guard/gate-pass?${params.toString()}`)
      .then(res => setRows(res.data || []))
      .catch(() => {
        if (!silent) Swal.fire({ icon: 'error', title: 'โหลดรายการไม่สำเร็จ', text: 'กรุณาลองใหม่อีกครั้ง' });
      })
      .finally(() => setLoading(false));
  }, [date, searchTerm]);

  const fetchOpen = useCallback(() => {
    axiosInstance.get('/api/guard/open-passes')
      .then(res => setOpenRows(res.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchOpen(); }, [fetchOpen]);

  // ยามเปิดหน้าจอค้างไว้ทั้งวัน — ดึงรายการใหม่เองทุก 30 วินาที เพื่อให้เห็นรายการที่เพิ่งอนุมัติ
  useEffect(() => {
    const timer = setInterval(() => { fetchList(true); fetchOpen(); }, 30000);
    return () => clearInterval(timer);
  }, [fetchList, fetchOpen]);

  const counts = useMemo(() => rows.reduce((acc, req) => {
    const state = gateState(req);
    if (state === 'ready') acc.ready += 1;
    else if (state === 'out') acc.out += 1;
    else if (state === 'done') acc.done += 1;
    else acc.hold += 1;
    return acc;
  }, { ready: 0, out: 0, done: 0, hold: 0 }), [rows]);

  const visibleRows = useMemo(() => {
    if (tab === 'open') return openRows;
    return rows.filter(req => {
      const state = gateState(req);
      if (tab === 'all') return true;
      if (tab === 'hold') return state === 'waiting' || state === 'blocked';
      return state === tab;
    });
  }, [rows, openRows, tab]);

  const patchRow = (req, changes) => {
    const apply = (list) => list.map(item => (passKey(item) === passKey(req) ? { ...item, ...changes } : item));
    setRows(apply);
    // กดกลับเข้าจากแท็บ "ยังไม่กลับ" แล้วต้องหลุดออกจากรายการทันที ไม่ต้องรอรอบรีเฟรช
    setOpenRows(prev => apply(prev).filter(item => !item.checked_in_at));
  };

  const submitSearch = (e) => {
    e.preventDefault();
    setSearchTerm(keyword.trim());
    setTab('all');
  };

  const clearSearch = () => {
    setKeyword('');
    setSearchTerm('');
    setTab('ready');
  };

  const studentHtml = (req) => (
    `<div class="fs-5 fw-bold">${req.st_name}</div>`
    + `<div class="text-secondary">ม.${req.c_id}/${req.r_no} · ${req.st_idnumber}</div>`
    + `<div class="mt-2">${PASS_TYPE[req.pass_type].label}</div>`
  );

  const runGateAction = (req, url, body, changes, successTitle) => {
    setSavingKey(passKey(req));
    axiosInstance.put(url, body)
      .then(res => {
        patchRow(req, typeof changes === 'function' ? changes(res.data) : changes);
        if (successTitle) Swal.fire({ icon: 'success', title: successTitle, timer: 1200, showConfirmButton: false });
      })
      .catch(err => {
        Swal.fire({ icon: 'error', title: 'บันทึกไม่สำเร็จ', text: err.response?.data?.message || 'กรุณาลองใหม่' });
        fetchList(true);
      })
      .finally(() => setSavingKey(null));
  };

  const confirmCheckOut = (req) => {
    Swal.fire({
      icon: 'question',
      title: 'ยืนยันให้นักเรียนออก?',
      html: `${studentHtml(req)}<div>ผู้รับ: ${req.guardian_name || '-'}</div>`,
      showCancelButton: true,
      confirmButtonText: 'ออกแล้ว',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#198754',
      reverseButtons: true
    }).then(result => {
      if (!result.isConfirmed) return;
      runGateAction(
        req,
        '/api/guard/gate-pass/check-out',
        { passType: req.pass_type, passId: req.pass_id },
        data => ({ checked_out_at: data.checked_out_at, checked_out_by_name: data.checked_out_by_name }),
        'บันทึกการออกแล้ว'
      );
    });
  };

  const confirmCheckIn = (req) => {
    Swal.fire({
      icon: 'question',
      title: 'นักเรียนกลับเข้าโรงเรียนแล้ว?',
      html: `${studentHtml(req)}<div>ออกเมื่อ ${thaiTime(req.checked_out_at)} น.</div>`,
      showCancelButton: true,
      confirmButtonText: 'กลับเข้าแล้ว',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#0d6efd',
      reverseButtons: true
    }).then(result => {
      if (!result.isConfirmed) return;
      runGateAction(
        req,
        '/api/guard/gate-pass/check-in',
        { passType: req.pass_type, passId: req.pass_id },
        data => ({ checked_in_at: data.checked_in_at, checked_in_by_name: data.checked_in_by_name }),
        'บันทึกการกลับเข้าแล้ว'
      );
    });
  };

  const confirmUndoCheckOut = (req) => {
    Swal.fire({
      icon: 'warning',
      title: 'ยกเลิกการบันทึกออก?',
      text: `${req.st_name} จะกลับไปอยู่ในรายการรอออก`,
      showCancelButton: true,
      confirmButtonText: 'ยกเลิกการออก',
      cancelButtonText: 'ปิด',
      confirmButtonColor: '#dc3545',
      reverseButtons: true
    }).then(result => {
      if (!result.isConfirmed) return;
      runGateAction(
        req,
        '/api/guard/gate-pass/undo-check-out',
        { passType: req.pass_type, passId: req.pass_id },
        { checked_out_at: null, checked_out_by_name: null }
      );
    });
  };

  const confirmUndoCheckIn = (req) => {
    Swal.fire({
      icon: 'warning',
      title: 'ยกเลิกการบันทึกกลับเข้า?',
      text: `${req.st_name} จะกลับไปอยู่ในรายการรอกลับเข้า`,
      showCancelButton: true,
      confirmButtonText: 'ยกเลิกการกลับเข้า',
      cancelButtonText: 'ปิด',
      confirmButtonColor: '#dc3545',
      reverseButtons: true
    }).then(result => {
      if (!result.isConfirmed) return;
      runGateAction(
        req,
        '/api/guard/gate-pass/undo-check-in',
        { passType: req.pass_type, passId: req.pass_id },
        { checked_in_at: null, checked_in_by_name: null }
      );
    });
  };

  const tabs = [
    { key: 'ready', label: 'รอออก', count: counts.ready, variant: 'success' },
    { key: 'out', label: 'รอกลับเข้า', count: counts.out, variant: 'warning' },
    { key: 'open', label: 'ยังไม่กลับ (ทุกวัน)', count: openRows.length, variant: 'danger' },
    { key: 'done', label: 'เสร็จสิ้น', count: counts.done, variant: 'secondary' },
    { key: 'hold', label: 'ห้ามออก', count: counts.hold, variant: 'danger' },
    { key: 'all', label: 'ทั้งหมด', count: rows.length, variant: 'dark' }
  ];

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-2 p-lg-4">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div>
            <h4 className="main-title mb-0">จุดตรวจประตูโรงเรียน</h4>
            <div className="fs-xs text-secondary">ตรวจใบอนุญาต กดยืนยันเมื่อออก และกดกลับเข้าเมื่อนักเรียนออกนอกสถานที่กลับมา</div>
          </div>
          <Button variant="outline-secondary" onClick={() => fetchList()} disabled={loading} aria-label="รีเฟรช">
            {loading ? <Spinner size="sm" animation="border" /> : <i className="ri-refresh-line"></i>}
          </Button>
        </div>

        <Card className="card-one mb-3">
          <Card.Body className="p-2 p-md-3">
            <Form onSubmit={submitSearch}>
              <InputGroup size="lg">
                <Form.Control
                  value={keyword}
                  onChange={e => setKeyword(e.target.value)}
                  placeholder="ค้นหา เลขประจำตัว / ชื่อนักเรียน"
                  inputMode="search"
                />
                {searchTerm && (
                  <Button variant="outline-secondary" type="button" onClick={clearSearch} aria-label="ล้างการค้นหา">
                    <i className="ri-close-line"></i>
                  </Button>
                )}
                <Button type="submit"><i className="ri-search-line"></i></Button>
              </InputGroup>
            </Form>

            {searchTerm ? (
              <div className="mt-2 fs-sm">
                ผลค้นหา “{searchTerm}” (ทุกวัน) · <Button variant="link" size="sm" className="p-0 align-baseline" onClick={clearSearch}>กลับไปรายการของวันนี้</Button>
              </div>
            ) : (
              <Row className="g-2 mt-1 align-items-center">
                <Col xs={7} sm={5} md={4}>
                  <Form.Control type="date" value={date} onChange={e => setDate(e.target.value)} />
                </Col>
                <Col xs={5} sm={7} md={8} className="fs-sm text-secondary">
                  {date === todayISO() ? 'รายการของวันนี้' : `รายการวันที่ ${thaiDate(date)}`}
                </Col>
              </Row>
            )}

            <div className="d-flex flex-wrap gap-2 mt-3">
              {tabs.map(item => (
                <Button
                  key={item.key}
                  size="sm"
                  variant={tab === item.key ? item.variant : `outline-${item.variant}`}
                  onClick={() => setTab(item.key)}
                >
                  {item.label} <Badge bg="light" text="dark" className="ms-1">{item.count}</Badge>
                </Button>
              ))}
            </div>
          </Card.Body>
        </Card>

        {loading && rows.length === 0 && (
          <div className="text-center text-secondary py-5"><Spinner animation="border" /></div>
        )}

        {!loading && visibleRows.length === 0 && (
          <Card className="card-one">
            <Card.Body className="text-center text-secondary py-5">
              <i className="ri-inbox-line" style={{ fontSize: 40 }}></i>
              <div className="mt-2">ไม่มีรายการในหมวดนี้</div>
            </Card.Body>
          </Card>
        )}

        <Row className="g-3" xs={1} lg={2} xxl={3}>
          {visibleRows.map(req => {
            const state = gateState(req);
            const style = STATE_STYLE[state];
            const type = PASS_TYPE[req.pass_type];
            const overdue = isOverdue(req);
            const saving = savingKey === passKey(req);
            return (
              <Col key={passKey(req)}>
                <Card className={`card-one h-100 border ${overdue ? 'border-danger' : style.border}`} style={{ borderWidth: 2 }}>
                  <Card.Body className="p-3">
                    <div className="d-flex align-items-center gap-3">
                      <StudentPhoto photo={req.st_photo} name={req.st_name} />
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <Badge bg={type.variant} className="mb-1">
                          <i className={`${type.icon} me-1`}></i>{type.label}
                        </Badge>
                        <div className="fw-bold text-truncate" style={{ fontSize: '1.05rem' }}>{req.st_name}</div>
                        <div className="fs-xs text-secondary">{req.st_idnumber} · ม.{req.c_id}/{req.r_no}</div>
                      </div>
                    </div>

                    <div className={`alert alert-${overdue ? 'danger' : style.badge} py-2 px-3 my-3 mb-2 fw-semibold text-center`}>
                      {overdue ? 'เลยเวลากลับที่ขอไว้ — ยังไม่กลับเข้าโรงเรียน' : style.label}
                      {req.checked_out_at && (
                        <div className="fs-xs fw-normal mt-1">
                          ออก {thaiTime(req.checked_out_at)} น. · {req.checked_out_by_name || 'เจ้าหน้าที่'}
                        </div>
                      )}
                      {req.checked_in_at && (
                        <div className="fs-xs fw-normal">
                          กลับเข้า {thaiTime(req.checked_in_at)} น. · {req.checked_in_by_name || 'เจ้าหน้าที่'}
                        </div>
                      )}
                    </div>

                    <dl className="row mb-0 fs-sm g-0">
                      {req.pass_type === 'outing' ? (
                        <>
                          <dt className="col-4 text-secondary fw-normal">วัน/เวลา</dt>
                          <dd className="col-8 mb-1">{thaiDate(req.pass_date)} · {req.out_time} - {req.expect_return_time} น.</dd>
                          {req.destination && (
                            <>
                              <dt className="col-4 text-secondary fw-normal">สถานที่</dt>
                              <dd className="col-8 mb-1">{req.destination}</dd>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <dt className="col-4 text-secondary fw-normal">ออก-กลับ</dt>
                          <dd className="col-8 mb-1">{thaiDate(req.pass_date)} – {req.return_date ? thaiDate(req.return_date) : 'ยังไม่กำหนดวันกลับ'}</dd>
                        </>
                      )}
                      <dt className="col-4 text-secondary fw-normal">เหตุผล</dt>
                      <dd className="col-8 mb-1">{req.reason}</dd>
                      <dt className="col-4 text-secondary fw-normal">ผู้รับ</dt>
                      <dd className="col-8 mb-1">
                        {req.guardian_name || '-'}
                        {req.guardian_phone && (
                          <a href={`tel:${req.guardian_phone}`} className="ms-2 text-nowrap">
                            <i className="ri-phone-line"></i> {req.guardian_phone}
                          </a>
                        )}
                      </dd>
                      {req.approved_by_name && (
                        <>
                          <dt className="col-4 text-secondary fw-normal">ผู้อนุมัติ</dt>
                          <dd className="col-8 mb-1">{req.approved_by_name}</dd>
                        </>
                      )}
                      {req.note && (
                        <>
                          <dt className="col-4 text-secondary fw-normal">หมายเหตุ</dt>
                          <dd className="col-8 mb-1">{req.note}</dd>
                        </>
                      )}
                    </dl>

                    {state === 'ready' && (
                      <Button
                        variant="success"
                        className="w-100 mt-3 fw-bold"
                        style={{ minHeight: 52, fontSize: '1.05rem' }}
                        disabled={saving}
                        onClick={() => confirmCheckOut(req)}
                      >
                        {saving ? <Spinner size="sm" animation="border" /> : <><i className="ri-door-open-line me-2"></i>ออกแล้ว</>}
                      </Button>
                    )}

                    {state === 'out' && (
                      <>
                        <Button
                          variant="primary"
                          className="w-100 mt-3 fw-bold"
                          style={{ minHeight: 52, fontSize: '1.05rem' }}
                          disabled={saving}
                          onClick={() => confirmCheckIn(req)}
                        >
                          {saving ? <Spinner size="sm" animation="border" /> : <><i className="ri-login-circle-line me-2"></i>กลับเข้าแล้ว</>}
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="w-100 mt-2"
                          disabled={saving}
                          onClick={() => confirmUndoCheckOut(req)}
                        >
                          ยกเลิกการบันทึกออก
                        </Button>
                      </>
                    )}

                    {state === 'done' && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="w-100 mt-3"
                        disabled={saving}
                        onClick={() => (req.pass_type === 'outing' ? confirmUndoCheckIn(req) : confirmUndoCheckOut(req))}
                      >
                        {req.pass_type === 'outing' ? 'ยกเลิกการบันทึกกลับเข้า' : 'ยกเลิกการบันทึกออก'}
                      </Button>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>

        <Footer />
      </div>
    </>
  );
}
